import { useState } from 'react'
import { useCreateRequest } from '../lib/createRequest'
import { useQueryClient } from '@tanstack/react-query'
import {
  useCreateProject,
  useCustomers,
  useDevices,
  usePriorities,
  useProcedures,
  useProjectStatuses,
  useProjectTemplates,
  useProjects,
  useTeams,
  useUsers
} from '../api/queries'
import type { Customer, ProjectDetail } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { ProjectCard } from '../components/ProjectCard'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { CaseNumberField } from '../components/CaseNumberField'
import { NewDeviceDrafts, toNewDeviceRequest, type DeviceDraft } from '../components/NewDeviceDrafts'
import { CustomerForm } from './CustomersPage'
import { TemplateOutline } from './admin/TemplatesPage'

/** Projektlisten med filtrering, arkiv og oprettelse af nye sager. */
export function ProjectsPage() {
  const { isProjectManager } = useAuth()
  const [search, setSearch] = useState('')
  const [statusId, setStatusId] = useState('')
  const [customerId, setCustomerId] = useState('')
  // Sag eller Service (beslutning 076). Tom = begge.
  const [kind, setKind] = useState('')
  const [archived, setArchived] = useState(false)
  // "Færdige sager" (beslutning 149): sager med status Færdig står for sig selv, så listen viser det, der er i gang.
  const [view, setView] = useState<'active' | 'done'>('active')
  const [showForm, setShowForm] = useState(false)
  // Sag eller servicesag - "+"-menuen kan bede om begge.
  const [formKind, setFormKind] = useState<'project' | 'service'>('project')
  useCreateRequest(['sag', 'servicesag'], (kind) => {
    if (!isProjectManager) return
    setFormKind(kind === 'servicesag' ? 'service' : 'project')
    setShowForm(true)
  })
  // Sagen blev oprettet, men noget omkring filerne gik galt - det må ikke gå ubemærket hen.
  const [warning, setWarning] = useState<string | null>(null)

  const { data: statuses } = useProjectStatuses()
  const { data: customers } = useCustomers()
  const { data, isLoading, error } = useProjects({
    search: search || undefined,
    statusId: statusId || undefined,
    customerId: customerId || undefined,
    kind: kind || undefined,
    archived,
    done: view === 'done'
  })

  return (
    <>
      <div className="row" style={{ gap: 6, marginBottom: 12 }} role="tablist">
        {(
          [
            ['active', 'Sager'],
            ['done', '✅ Færdige sager']
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            className={`btn btn--sm${view === key ? ' btn--primary' : ''}`}
            aria-selected={view === key}
            onClick={() => {
              setView(key)
              setStatusId('')
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="row" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          style={{ maxWidth: 240 }}
          placeholder="Søg i sager ..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {view === 'active' && (
          <select style={{ maxWidth: 170 }} value={statusId} onChange={(event) => setStatusId(event.target.value)}>
            <option value="">Alle statusser</option>
            {statuses
              ?.filter((status) => status.systemCode !== 'done')
              .map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
          </select>
        )}
        <select style={{ maxWidth: 150 }} value={kind} onChange={(event) => setKind(event.target.value)}>
          <option value="">Alle typer</option>
          <option value="project">Sager</option>
          <option value="service">Service</option>
        </select>
        <select style={{ maxWidth: 190 }} value={customerId} onChange={(event) => setCustomerId(event.target.value)}>
          <option value="">Alle kunder</option>
          {customers?.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
            </option>
          ))}
        </select>
        <label className="row small muted" style={{ gap: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={archived}
            onChange={(event) => setArchived(event.target.checked)}
          />
          Vis arkiv
        </label>

        {isProjectManager && (
          <button className="btn btn--primary" style={{ marginLeft: 'auto' }} onClick={() => {
            setFormKind('project')
            setShowForm((v) => !v)
          }}>
            {showForm ? 'Luk' : 'Ny sag'}
          </button>
        )}
      </div>

      {warning && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          {warning}
        </div>
      )}

      {showForm && (
        <NewProjectForm
          key={formKind}
          initialKind={formKind}
          onDone={(message) => {
            setShowForm(false)
            setWarning(message ?? null)
          }}
        />
      )}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {data && data.items.length === 0 && (
        <EmptyState>{view === 'done' ? 'Ingen færdige sager matcher filteret.' : 'Ingen sager matcher filteret.'}</EmptyState>
      )}

      {data && data.items.length > 0 && (
        <div className="grid grid--projects">
          {data.items.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
    </>
  )
}

/**
 * Formularen til en ny sag. Bruges både under Sager og under Service, hvor typen er
 * låst til servicesag (`initialKind="service"` + `lockKind`).
 *
 * En servicesag skal have en kunde, for enhederne hører til kunden (beslutning 024 og
 * 076). Kundens enheder kan vælges med det samme; de sættes på sagen lige efter, at den
 * er oprettet, med det samme kald som "Vælg enheder" på sagen.
 */
export function NewProjectForm({
  onDone,
  initialKind = 'project',
  lockKind = false,
  onCreated
}: {
  onDone: (warning?: string) => void
  initialKind?: 'project' | 'service'
  lockKind?: boolean
  /** Kaldes i stedet for `onDone`, når sagen er oprettet. `warning` er sat, hvis enhederne ikke kom på. */
  onCreated?: (created: ProjectDetail, warning?: string) => void
}) {
  const createProject = useCreateProject()
  const queryClient = useQueryClient()
  const { data: users } = useUsers()
  const { data: teams } = useTeams()
  const { data: priorities } = usePriorities()
  const { data: statuses } = useProjectStatuses()
  const { data: customers } = useCustomers()
  const { data: templates } = useProjectTemplates()

  const [form, setForm] = useState({
    // Ordrenummeret i Ordrestyring - påkrævet og formularens første felt (beslutning 077).
    orderNumber: '',
    // Kundens eget sagsnummer (beslutning 083). Valgfrit: lades det tomt, danner
    // serveren det af ordrenummeret.
    customerCaseNumber: '',
    name: '',
    kind: initialKind as string,
    customerId: '',
    templateId: '',
    description: '',
    projectManagerId: '',
    teamId: '',
    startDate: '',
    deadline: '',
    priorityId: '',
    statusId: '',
    nextActionText: '',
    waitingReason: '',
    // Arbejdsstedet, naar det ikke er kundens adresse (beslutning 044).
    siteAddress: '',
    sitePostalCode: '',
    siteCity: '',
    // Rettigheder i ampere der, hvor arbejdet foregår (beslutning 109).
    connectionAmps: ''
  })
  // Nye enheder, der oprettes sammen med sagen, fx fire batterier (beslutning 109).
  const [drafts, setDrafts] = useState<DeviceDraft[]>([])
  // Medlemmerne står for sig, fordi resten af formularen er strenge fra input-felter.
  const [memberIds, setMemberIds] = useState<number[]>([])
  // Undefined = brugeren har ikke rørt feltet, så systemets standardindstilling gælder.
  const [error, setError] = useState<string | null>(null)
  const [newCustomer, setNewCustomer] = useState(false)
  // Enhederne på servicesagen. Nulstilles, når kunden skiftes - de hører til kunden.
  const [deviceIds, setDeviceIds] = useState<number[]>([])

  const isService = form.kind === 'service'
  const { data: customerDevices } = useDevices(
    { customerId: form.customerId ? Number(form.customerId) : undefined },
    isService && Boolean(form.customerId)
  )

  const selectedStatus = statuses?.find((s) => String(s.id) === form.statusId)
  const selectedTemplate = templates?.find((t) => String(t.id) === form.templateId)

  // Først proceduren (typen af sag), så en af dens skabeloner (beslutning 141).
  // 'none' = skabeloner fra før procedurerne.
  const { data: procedures } = useProcedures()
  const [procedureId, setProcedureId] = useState('')
  const hasLegacyTemplates = templates?.some((t) => !t.procedureId) ?? false
  const procedureTemplates =
    templates?.filter((t) => (procedureId === 'none' ? !t.procedureId : String(t.procedureId) === procedureId)) ?? []

  function chooseProcedure(value: string) {
    setProcedureId(value)
    const matching =
      templates?.filter((t) => (value === 'none' ? !t.procedureId : String(t.procedureId) === value)) ?? []
    // Har proceduren kun én skabelon, vælges den med det samme.
    update('templateId', matching.length === 1 ? String(matching[0].id) : '')
  }

  function update(field: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
    if (field === 'customerId' || field === 'kind') setDeviceIds([])
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    let created: ProjectDetail
    try {
      created = await createProject.mutateAsync({
        orderNumber: form.orderNumber,
        customerCaseNumber: form.customerCaseNumber || null,
        name: form.name,
        kind: form.kind,
        customerId: form.customerId ? Number(form.customerId) : null,
        templateId: form.templateId ? Number(form.templateId) : null,
        description: form.description || null,
        projectManagerId: form.projectManagerId ? Number(form.projectManagerId) : null,
        teamId: form.teamId ? Number(form.teamId) : null,
        startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        priorityId: form.priorityId ? Number(form.priorityId) : null,
        statusId: form.statusId ? Number(form.statusId) : null,
        nextActionText: form.nextActionText || null,
        waitingReason: form.waitingReason || null,
        siteAddress: form.siteAddress || null,
        sitePostalCode: form.sitePostalCode || null,
        siteCity: form.siteCity || null,
        connectionAmps: form.connectionAmps ? Number(form.connectionAmps) : null,
        memberIds,
        // Enhederne oprettes i samme kald som sagen (beslutning 109) - så går det hele igennem eller intet.
        serviceDeviceIds: isService ? deviceIds : [],
        newDevices: drafts.map(toNewDeviceRequest)
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sagen kunne ikke oprettes.')
      return
    }

    if (drafts.length > 0 || deviceIds.length > 0) {
      void queryClient.invalidateQueries({ queryKey: ['devices'] })
    }

    if (onCreated) onCreated(created)
    else onDone()
  }

  if (newCustomer) {
    return (
      <CustomerForm
        customer={null}
        onClose={() => setNewCustomer(false)}
        onSaved={(customer: Customer) => update('customerId', String(customer.id))}
      />
    )
  }

  return (
    <div style={{ marginBottom: 18 }}>
      <Card title={isService ? 'Ny servicesag' : 'Ny sag'}>
        <form onSubmit={submit}>
          <div className="form-grid">
            {/* Ordrenummeret først: uden det kan sagen ikke findes i Ordrestyring, og
                serveren opretter den ikke (beslutning 077). */}
            <label className="field">
              <span className="field__label">Ordrenummer *</span>
              <input
                required
                autoFocus
                // Ikke inputMode="numeric": telefonens taltastatur har ingen bindestreg,
                // og underordrer skrives "4850-1" (beslutning 084).
                pattern="[0-9]+(-[0-9]+)*"
                title="Cifre og bindestreg, som nummeret står i Ordrestyring - f.eks. 4850-1"
                placeholder="Fra Ordrestyring, f.eks. 4850-1"
                value={form.orderNumber}
                onChange={(e) => update('orderNumber', e.target.value)}
              />
            </label>

            {/* Kundens eget nummer staar ved siden af vores. Valgfrit: lades det
                tomt, danner serveren det af ordrenummeret (beslutning 083). */}
            <CaseNumberField
              value={form.customerCaseNumber}
              onChange={(value) => update('customerCaseNumber', value)}
              customerId={form.customerId ? Number(form.customerId) : null}
              orderNumber={form.orderNumber}
            />

            <label className="field">
              <span className="field__label">Sagsnavn *</span>
              <input required value={form.name} onChange={(e) => update('name', e.target.value)} />
            </label>

            {!lockKind && (
              <label className="field">
                <span className="field__label">Type</span>
                <select value={form.kind} onChange={(e) => update('kind', e.target.value)}>
                  <option value="project">Sag</option>
                  <option value="service">Service - hos en kunde med enheder</option>
                </select>
                {isService && (
                  <span className="tiny muted">
                    Vælg kunden og derefter de enheder, servicen gælder.
                  </span>
                )}
              </label>
            )}

            <label className="field">
              <span className="field__label">Kunde{isService ? ' *' : ''}</span>
              <div className="row">
                {/* En servicesag uden kunde kan ikke få enheder - de hører til kunden. */}
                <select
                  required={isService}
                  value={form.customerId}
                  onChange={(e) => update('customerId', e.target.value)}
                >
                  <option value="">{isService ? 'Vælg kunde' : 'Ingen kunde'}</option>
                  {customers?.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                      {customer.city ? ` (${customer.city})` : ''}
                    </option>
                  ))}
                </select>
                <button className="btn btn--sm" type="button" onClick={() => setNewCustomer(true)}>
                  Ny
                </button>
              </div>
            </label>

            {isService && form.customerId && (
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <span className="field__label">
                  Enheder på servicesagen{' '}
                  {deviceIds.length > 0 && <span className="tiny muted">({deviceIds.length} valgt)</span>}
                </span>
                {!customerDevices ? (
                  <span className="tiny muted">Henter kundens enheder ...</span>
                ) : customerDevices.length === 0 ? (
                  <span className="tiny muted">
                    Der står ingen enheder hos kunden. Sagen kan oprettes alligevel - enhederne
                    oprettes under Enheder og vælges bagefter på sagen.
                  </span>
                ) : (
                  <div className="cal-people">
                    {customerDevices.map((device) => (
                      <label
                        key={device.id}
                        className={`cal-person ${deviceIds.includes(device.id) ? 'cal-person--on' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={deviceIds.includes(device.id)}
                          onChange={() =>
                            setDeviceIds((current) =>
                              current.includes(device.id)
                                ? current.filter((x) => x !== device.id)
                                : [...current, device.id]
                            )
                          }
                        />
                        <span>
                          {device.name}
                          {device.location ? ` · ${device.location}` : ''}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Arbejdsstedet. Tomt = hos kunden, som er det normale - derfor staar
                det efter kunden og ikke som noget, man skal tage stilling til. */}
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <span className="field__label">Hvor foregår arbejdet?</span>
              <div className="site-fields">
                <input
                  placeholder="Vej og nr. - tom hvis det er hos kunden"
                  value={form.siteAddress}
                  onChange={(e) => update('siteAddress', e.target.value)}
                />
                <input
                  placeholder="Postnr."
                  style={{ maxWidth: 110 }}
                  value={form.sitePostalCode}
                  onChange={(e) => update('sitePostalCode', e.target.value)}
                />
                <input
                  placeholder="By"
                  value={form.siteCity}
                  onChange={(e) => update('siteCity', e.target.value)}
                />
              </div>
              <span className="tiny muted">
                Udfyld kun, når arbejdet ikke foregår på kundens egen adresse - f.eks. en
                ejendom eller en byggeplads. Ellers bruges kundens adresse på kortet.
              </span>
            </div>

            {/* Rettighederne hører til stedet: sagens egen adresse, ellers kunden (beslutning 109). */}
            <label className="field">
              <span className="field__label">Rettigheder (A)</span>
              <input
                type="number"
                min={1}
                max={10000}
                inputMode="numeric"
                placeholder="f.eks. 63"
                value={form.connectionAmps}
                onChange={(e) => update('connectionAmps', e.target.value)}
              />
              <span className="tiny muted">
                {form.siteAddress.trim()
                  ? 'Gemmes på sagens adresse.'
                  : form.customerId
                    ? 'Gemmes på kunden - arbejdet foregår på kundens adresse.'
                    : 'Vælg en kunde eller skriv en adresse, så tallet har et sted at stå.'}
              </span>
            </label>

            {/* Nye enheder på sagen, fx fire batterier eller en ny tavle (beslutning 109). */}
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <span className="field__label">
                Nye enheder på sagen {drafts.length > 0 && <span className="tiny muted">({drafts.length})</span>}
              </span>
              {form.customerId ? (
                <NewDeviceDrafts drafts={drafts} onChange={setDrafts} />
              ) : (
                <span className="tiny muted">Vælg kunden først - enhederne hører til kunden.</span>
              )}
              <span className="tiny muted">
                Enhederne oprettes sammen med sagen. Serienummer og dokumenter, fx tavletegninger, kan lægges på
                enheden bagefter.
              </span>
            </div>

            <label className="field">
              <span className="field__label">Procedure (typen af sag)</span>
              <select value={procedureId} onChange={(e) => chooseProcedure(e.target.value)}>
                <option value="">Ingen - tom sag</option>
                {procedures
                  ?.filter((procedure) => procedure.templateCount > 0)
                  .map((procedure) => (
                    <option key={procedure.id} value={procedure.id}>
                      {procedure.name}
                    </option>
                  ))}
                {hasLegacyTemplates && <option value="none">Uden procedure</option>}
              </select>
            </label>

            {procedureId && (
              <label className="field">
                <span className="field__label">Skabelon (opretter opgaverne)</span>
                <select value={form.templateId} onChange={(e) => update('templateId', e.target.value)}>
                  <option value="">Vælg skabelon</option>
                  {procedureTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.taskCount} underemner)
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="field">
              <span className="field__label">Projektleder</span>
              <select value={form.projectManagerId} onChange={(e) => update('projectManagerId', e.target.value)}>
                <option value="">Mig selv</option>
                {users?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field__label">Team</span>
              <select value={form.teamId} onChange={(e) => update('teamId', e.target.value)}>
                <option value="">Intet team</option>
                {teams?.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field__label">Startdato {selectedTemplate && <span className="muted">(styrer opgavernes deadlines)</span>}</span>
              <input type="date" value={form.startDate} onChange={(e) => update('startDate', e.target.value)} />
            </label>

            <label className="field">
              <span className="field__label">Deadline</span>
              <input type="date" value={form.deadline} onChange={(e) => update('deadline', e.target.value)} />
            </label>

            <label className="field">
              <span className="field__label">Prioritet</span>
              <select value={form.priorityId} onChange={(e) => update('priorityId', e.target.value)}>
                <option value="">{selectedTemplate?.defaultPriority?.name ?? 'Normal'}</option>
                {priorities?.map((priority) => (
                  <option key={priority.id} value={priority.id}>
                    {priority.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field__label">Status</span>
              <select value={form.statusId} onChange={(e) => update('statusId', e.target.value)}>
                <option value="">Ikke startet</option>
                {statuses?.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field__label">Næste handling</span>
              <input value={form.nextActionText} onChange={(e) => update('nextActionText', e.target.value)} />
            </label>
          </div>

          {selectedTemplate && (
            <div className="card card--flat" style={{ padding: 12, marginBottom: 12 }}>
              <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>
                Disse {selectedTemplate.taskCount} underemner oprettes som opgaver:
              </div>
              <TemplateOutline template={selectedTemplate} />
              {!form.startDate && selectedTemplate.tasks.some((t) => t.deadlineOffsetDays != null) && (
                <div className="tiny warn" style={{ marginTop: 6 }}>
                  Sæt en startdato for at få deadlines på opgaverne.
                </div>
              )}
            </div>
          )}

          {selectedStatus?.isWaiting && (
            <label className="field">
              <span className="field__label">Afventer årsag *</span>
              <input required value={form.waitingReason} onChange={(e) => update('waitingReason', e.target.value)} />
            </label>
          )}

          <div className="field">
            <span className="field__label">
              Medarbejdere på sagen{' '}
              {memberIds.length > 0 && <span className="tiny muted">({memberIds.length} valgt)</span>}
            </span>
            <div className="cal-people">
              {users?.map((u) => (
                <label key={u.id} className={`cal-person ${memberIds.includes(u.id) ? 'cal-person--on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={memberIds.includes(u.id)}
                    onChange={() =>
                      setMemberIds((current) =>
                        current.includes(u.id) ? current.filter((x) => x !== u.id) : [...current, u.id]
                      )
                    }
                  />
                  <span>{u.displayName}</span>
                </label>
              ))}
            </div>
            <span className="tiny muted">
              De valgte kan se sagen på deres egen liste fra start. Projektlederen og alle med en
              opgave på sagen kan se den i forvejen - det her er til dem, der skal med, uden at
              have en opgave endnu. Kan ændres senere på sagen.
            </span>
          </div>

          <label className="field">
            <span className="field__label">Beskrivelse</span>
            <textarea value={form.description} onChange={(e) => update('description', e.target.value)} />
          </label>

          {error && <div className="error-box" style={{ marginBottom: 12 }}>{error}</div>}

          <div className="btn-group">
            <button className="btn btn--primary" type="submit" disabled={createProject.isPending}>
              {createProject.isPending ? 'Opretter ...' : isService ? 'Opret servicesag' : 'Opret sag'}
            </button>
            <button className="btn" type="button" onClick={() => onDone()}>
              Fortryd
            </button>
          </div>
        </form>
      </Card>
    </div>
  )
}
